"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DAY_PART_LABELS = exports.DAY_PARTS = exports.WEEKDAYS = void 0;
exports.isAsNeeded = isAsNeeded;
exports.medLabelFor = medLabelFor;
exports.weekdayOf = weekdayOf;
exports.buildDaySchedule = buildDaySchedule;
exports.asNeededMedications = asNeededMedications;
exports.dayPartFor = dayPartFor;
exports.buildDayGrid = buildDayGrid;
const time_js_1 = require("./time.js");
exports.WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
/**
 * As-needed medications appear on the screen as a button, never as a due dose.
 *
 * This is the load-bearing consequence of not knowing ondansetron's frequency: it
 * produces no scheduled doses, so it can never be reported missed. A false
 * missed-dose alert on a PRN drug teaches everyone to ignore alerts, which destroys
 * the alerts that matter.
 */
function isAsNeeded(scheduleType) {
    return scheduleType === 'AS_NEEDED' || scheduleType === 'AS_NEEDED_UNCONFIRMED';
}
function medLabelFor(med) {
    const form = med.form.toLowerCase().replace(/_/g, ' ');
    return `${med.name} ${String(med.strength.value)} ${med.strength.unit} ${form}`;
}
/** Which weekday a logical date falls on, evaluated as a plain calendar date. */
function weekdayOf(logicalDate) {
    const [y, m, d] = logicalDate.split('-').map(Number);
    if (y === undefined || m === undefined || d === undefined) {
        throw new RangeError(`Invalid logical date: ${logicalDate}`);
    }
    // UTC noon avoids any chance of a timezone shifting the weekday.
    const day = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
    const weekday = exports.WEEKDAYS[day];
    if (weekday === undefined)
        throw new RangeError(`Invalid weekday index ${String(day)}`);
    return weekday;
}
/**
 * Every dose scheduled on `logicalDate`, in due order.
 *
 * Times are resolved as local wall-clock in the patient's zone, so a 07:00 dose stays
 * at 07:00 across a DST change rather than sliding to 06:00 or 08:00.
 */
function buildDaySchedule(medications, logicalDate, tz) {
    const weekday = weekdayOf(logicalDate);
    const doses = [];
    for (const med of medications) {
        if (isAsNeeded(med.scheduleType))
            continue;
        const days = med.daysOfWeek ?? null;
        if (days !== null && !days.includes(weekday))
            continue;
        for (const slot of med.slots) {
            const due = (0, time_js_1.localWallTimeToInstant)(logicalDate, slot.localTime, tz);
            const dueMs = due.getTime();
            doses.push({
                medId: med.medId,
                medVersion: med.version,
                medName: med.name,
                medLabel: medLabelFor(med),
                slotId: slot.slotId,
                slotLabel: slot.label,
                localTime: slot.localTime,
                quantity: slot.quantity,
                logicalDate,
                dueAtUtc: due.toISOString(),
                windowStartUtc: new Date(dueMs - slot.windowBeforeMin * 60_000).toISOString(),
                windowEndUtc: new Date(dueMs + slot.windowAfterMin * 60_000).toISOString(),
                confirmationStatus: med.confirmationStatus,
            });
        }
    }
    return doses.sort((a, b) => a.dueAtUtc.localeCompare(b.dueAtUtc));
}
/** Medications that are logged on demand rather than on a schedule. */
function asNeededMedications(medications) {
    return medications.filter((m) => isAsNeeded(m.scheduleType));
}
// ---------------------------------------------------------------------------
// The paper chart's grid
// ---------------------------------------------------------------------------
/**
 * The four columns of the paper chart. Kept so the caregiver month view is legible
 * to anyone who has seen the PDF.
 */
exports.DAY_PARTS = ['BEFORE_BREAKFAST', 'BEFORE_LUNCH', 'AFTER_DINNER', 'BEDTIME'];
exports.DAY_PART_LABELS = {
    BEFORE_BREAKFAST: 'Before breakfast',
    BEFORE_LUNCH: 'Before lunch',
    AFTER_DINNER: 'After dinner',
    BEDTIME: 'Bedtime',
};
/** Bucket a local time into the chart column a person would have written it in. */
function dayPartFor(localTime) {
    const hour = Number(localTime.slice(0, 2));
    if (!Number.isFinite(hour))
        throw new RangeError(`Invalid local time: ${localTime}`);
    if (hour < 10)
        return 'BEFORE_BREAKFAST';
    if (hour < 15)
        return 'BEFORE_LUNCH';
    if (hour < 20)
        return 'AFTER_DINNER';
    return 'BEDTIME';
}
/**
 * The chart row for one day, with every empty cell explicitly NOT_SCHEDULED.
 *
 * This enforces the rule from the source email — "if a medicine is not scheduled on a
 * particular day, block that box so it cannot look like a missed dose." Rendering an
 * absent cell as merely blank is what makes a doctor mistake a blocked slot for a
 * skipped one, so absence is stored as a status rather than left to CSS.
 */
function buildDayGrid(medications, doses) {
    const scheduled = new Map();
    for (const dose of doses)
        scheduled.set(`${dose.medId}|${dayPartFor(dose.localTime)}`, dose);
    const cells = [];
    for (const med of medications) {
        if (isAsNeeded(med.scheduleType))
            continue;
        for (const dayPart of exports.DAY_PARTS) {
            const dose = scheduled.get(`${med.medId}|${dayPart}`);
            cells.push(dose
                ? {
                    medId: med.medId,
                    dayPart,
                    status: 'OPEN',
                    slotId: dose.slotId,
                    slotLabel: dose.slotLabel,
                    localTime: dose.localTime,
                }
                : { medId: med.medId, dayPart, status: 'NOT_SCHEDULED' });
        }
    }
    return cells;
}
//# sourceMappingURL=schedule.js.map